import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import * as FlatModels from '@medsurf/flat-models';
import { MenuSharedFacade, NavigationControlFacade, SlideViewerFacade } from '@medsurf/flat-facades';
import { v4 } from 'uuid';
import { Store } from '@ngxs/store';
import { combineLatest, map, ReplaySubject } from 'rxjs';


const parseTime = (t: string | undefined) => {
  if (t === undefined) return Number.NaN;
  const parts = t.split(':').toReversed();
  if (parts.length >= 2 && parts.length <= 3) {
    return parts.reduce((acc, part, index) => {
      if (isNaN(acc)) return acc;
      return acc + 60 ** index * parseFloat(part)
    }, 0)
  } else {
    return parseFloat(t);
  }
}
const parseNumber = (t1: string | undefined, t2: string | undefined, fallback: number | null) => {
  const t = parseTime(t1 || t2);
  return isNaN(t) ? fallback : t;
}

@Component({
    selector: 'medsurf-text',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './text.component.html',
    styleUrls: ['./text.component.scss'],
    standalone: false
})
export class TextComponent {
  /**
   * Inputs
   */
  @Input() public set text(v) { this.text$.next(v) }
  public readonly text$ = new ReplaySubject<string>(1);
  @Input() public isSlideDescription: boolean;

  /**
   * View Children
   */
  @ViewChild('textElement', {static: false}) public textElement: ElementRef;

  /**
   * Members
   */
  private uuidRegExp = '[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}';
  public test$ = combineLatest([
    this.text$.pipe(map(text => this.parseInteractiveMedia(text))),
    this.slideViewerFacade.currentActiveEvents$,
    this.slideViewerFacade.currentSelectedSlideGroups$,
    this.slideViewerFacade.themeColor$
  ]).pipe(map(([text, events, groups, themeColor]) => {
    const preparedText = this.prepareLinks(text, themeColor, groups, events);
    return preparedText
  }))

  /**
   * Constructor
   *
   * @param navigationControlFacade
   * @param slideViewerFacade
   * @param menuSharedFacade
   */
  constructor(public navigationControlFacade: NavigationControlFacade,
              public slideViewerFacade: SlideViewerFacade,
              public menuSharedFacade: MenuSharedFacade,
              public store: Store) {
  }

  public prepareLinks(
    text: string, 
    themeColor: string, 
    groups: FlatModels.GroupEntityModels.Group[], 
    events: FlatModels.MediaInteractionViewerModels.MediaInteractionEvent[]
  ) {
    const el = document.createElement('div');
    el.innerHTML = text;

    const actionElements = Array.from(el.querySelectorAll('[data-mia-action]'));
    actionElements.forEach((el: HTMLElement) => {
      el.style.cursor = 'pointer';
    })

    events?.forEach(event => {
      if (event.type === 'highlight') {
        const highlight: HTMLElement = el.querySelector(`[data-mia-id="${event.id}"]`);
        if (!highlight) return;
        
        if (event.classes || event.styles) {
          if (event.classes) highlight.className += ` ${event.classes}`
          if (event.styles) highlight.style.cssText += event.styles;
        } else {
          highlight.classList.add('mds-highlight');
        }
        
        const detailsHighlight = highlight.tagName === 'DETAILS' ? highlight as HTMLDetailsElement : highlight.querySelector<HTMLDetailsElement>(':scope > details');
        if (detailsHighlight) { detailsHighlight.open = true; }

        // Open parent details of nested details
        let parentDetails = highlight.parentElement?.closest('details')
        while (parentDetails) {
          parentDetails.open = true;
          parentDetails = parentDetails.parentElement?.closest('details')
        }
      } else if (event.type === 'visible') {
        const visible: HTMLElement = el.querySelector(`[data-mia-id="${event.id}"]`);
        if (!visible) return;
        visible.style.display = '';
      }
    })

    const links = Array.from(el.getElementsByTagName('a'));
    links.forEach((link) => {
      if (link.classList.contains('mds-internal') || link.classList.contains('mds-external')) {
        link.classList.add(themeColor);
        link.target = link.target ?? '_blank';

      } else if (link.className === '') {
        link.classList.add(themeColor);
        link.target = link.target ?? '_blank';

      } else if (link.classList.contains('mds-marker')) {
        let updateColor = false;
        let href: string = link.href.replace(/^(http|https):\/\/([a-zA-Z0-9:.]*\/)+/, '');
        if (!href) {
          updateColor = true;
          href = link.id;
          if (!href) {
            return;
          }
        }
        if (link.innerText.trim() === '<') {
          link.innerHTML = '<i class="fa fa-location-dot"></i>';
        }

        // Layer
        let layerId = null;
        if (href.includes('layer')) {
          const layerRegex = new RegExp('layer-([0-9]+)', 'g');
          const layerResult = layerRegex.exec(href);
          if (layerResult && layerResult.length === 2) {
            layerId = layerResult[1];
          }
        }
        // Sequence
        let sequenceId = null;
        if (href.includes('sequence')) {
          const sequenceRegex = new RegExp('sequence-([0-9]+)', 'g');
          const sequenceResult = sequenceRegex.exec(href);
          if (sequenceResult && sequenceResult.length === 2) {
            sequenceId = sequenceResult[1];
          }
        }
        // Marker
        const markerRegex = new RegExp(`(group|annotation)-(${this.uuidRegExp})`);
        const markerResult = markerRegex.exec(href);
        if (markerResult && markerResult.length === 3) {
          href = (markerResult[0].includes('group') || markerResult[1].includes('annotation')) ?
            markerResult[0] :
            `annotation-${markerResult[2]}`;
        }

        const [type, ...idParts] = href.split('-');
        const id = idParts.join('-');

        if (type && id) {
          const group = groups.find(g => g.id === id);
          link.style.color = group && group.color ? group.color : themeColor;
          if (!updateColor) {
            link.id = href;
            link.type = 'group';
            link.removeAttribute('href');
            link.dataset.groupId = id;
            link.dataset.layerId = layerId;
            link.dataset.sequenceId = sequenceId;
            link.style.cursor = 'pointer';
          }
        }
      }
    })
    return el.innerHTML;
  }

  public onLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const el = (target.dataset.miaAction ? target : target.closest('[data-mia-action]')) as HTMLLinkElement | undefined;
    if (el) {
      const action = el.dataset.miaAction;
      const delimiter = '|';
      const parts = action.split(delimiter);
      const type = parts[0];
      const to = parseNumber(parts[1], el.dataset.miaTo, null);

      if (type === 'jump') {
        return to !== null ? this.slideViewerFacade.requestJumpToTime(to) : null;
      }
      
      if (type === 'play') {
        return to !== null ? this.slideViewerFacade.requestJumpToTime(to, 'play') : this.slideViewerFacade.requestTogglePlay('play');
      }

      if (type === 'pause') {
        return to !== null ? this.slideViewerFacade.requestJumpToTime(to, 'pause') : this.slideViewerFacade.requestTogglePlay('pause');
      }

      if (type === 'open-modal') {
        const target = parts[1] || el.dataset.miaTarget;
        const onCloseRaw = parts[2] || el.dataset.miaOnClose;
        const onClose = (onCloseRaw || '').split(':');
        const onCloseType = onClose[0];
        const onCloseValue = onClose[1];

        const onCloseParsedType = (onCloseType === 'play' || onCloseType === 'pause') ? onCloseType : undefined;
        const onCloseParsedValue = isNaN(Number(onCloseValue)) ? undefined : Number(onCloseValue);

        return this.slideViewerFacade.requestOpenModal(target, onCloseType ? { type: onCloseParsedType, value: onCloseParsedValue } : undefined);
      }
    }
  }

  public onLinkOut(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const el = (target.classList.contains('mds-marker') ? target : target.closest('.mds-marker')) as HTMLLinkElement | undefined;
    if (el) {
      this.slideViewerFacade.requestSetGroupId(null);
    }
  }

  public onLinkOver(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const el = (target.classList.contains('mds-marker') ? target : target.closest('.mds-marker')) as HTMLLinkElement | undefined;
    if (el) {
      const groupId = el.dataset.groupId;
      const layerId = el.dataset.layerId;
      const sequenceId = el.dataset.sequenceId;
      if (groupId) this.slideViewerFacade.requestSetGroupId(groupId);
      if (layerId) this.slideViewerFacade.requestSetLayerIndex(Number.parseInt(layerId, 10));
      if (sequenceId) this.slideViewerFacade.requestSetImageIndex(Number.parseInt(sequenceId, 10));
    }
  }

  public parseInteractiveMedia(text: string) {
    const textEl = document.createElement('div');


    // Replace data-autoplay with real autoplay attribute (prevents autoplay in editor)
    textEl.innerHTML = text.replaceAll('data-autoplay', 'autoplay');

    // Open details when generating pdf
    if(this.menuSharedFacade.snapshot_showPrint()) {
      textEl.querySelectorAll('details').forEach(details => {
        details.open = true;
      })
    }

    if (textEl) {
      const events: FlatModels.MediaInteractionViewerModels.MediaInteractionEvent[] = [];

      const mediaInteractionEventElements = Array.from(textEl.querySelectorAll('[data-mia-event]'));
      mediaInteractionEventElements.forEach((el: HTMLElement) => {
        const event = el.dataset.miaEvent;
        const id = el.dataset.miaId || v4();
        el.dataset.miaId = id;

        const groupDelimiter = '+';
        const delimiter = '|';

        const groups = event.split(groupDelimiter);
        groups.forEach((group) => {
          const parts = group.split(delimiter);
          const type = parts[0];

          if (type === 'visible') {
            const from = parseNumber(parts[1], el.dataset.miaFrom, null);
            const to = parseNumber(parts[2], el.dataset.miaTo, null); // 0 will be replaced with media duration
            el.style.display = 'none';
            return events.push({
              type,
              id,
              from,
              to
            })
          }

          if (type === 'highlight') {
            const from = parseNumber(parts[1], el.dataset.miaFrom, null);
            const to = parseNumber(parts[2], el.dataset.miaTo, null);
            const classes = parts[3] || el.dataset.miaClasses;
            const styles = parts[4] || el.dataset.miaStyles;
            return events.push({
              type,
              id,
              from,
              to,
              styles,
              classes
            })
          }
          
          if (type === 'modal') {
            const from = parseNumber(parts[1], el.dataset.miaFrom, null);
            const to = parseNumber(parts[2], el.dataset.miaTo, from === null ? null : from + 3); // If from not 0 fallback will be from + 3
            const title = parts[3] || el.dataset.miaTitle;
            const size = parts[4] || el.dataset.miaSize;
            const content = el.innerHTML;
            el.parentElement?.removeChild(el);

            // Append all modals to text when generating pdf
            if(this.menuSharedFacade.snapshot_showPrint()) {
              const modal = document.createElement('div');
              modal.className = 'modal-placeholder';
              modal.innerHTML = '<i class="fal fa-close close"></i>';
              if (title) modal.innerHTML += `<h4 class="modal-placeholder-title">${title}</h4>`;
              modal.innerHTML += `<div class="modal-placeholder-content">${content}</div>`;
              textEl.appendChild(modal);
            }

            return events.push({
              type,
              id,
              from,
              to,
              title,
              content,
              size
            })
          }
        });
      });
      this.slideViewerFacade.requestAddEvents(events);
    }
    return textEl.innerHTML;
  }
}